import { ExternalLink, FastForward, Loader2, Pause, Play, RotateCcw, Volume1, Volume2, VolumeX } from 'lucide-react';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Formats seconds into a stable, zero-padded time string.
 * When forceHours is true (or seconds >= 3600), always outputs HH:MM:SS (e.g. 00:00:00 or 04:01:30).
 * Otherwise outputs MM:SS (e.g. 00:00 or 45:30).
 */
export function formatAudioTime(seconds: number, forceHours = false): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return forceHours ? '00:00:00' : '00:00';
  }
  const totalSecs = Math.floor(seconds);
  const hrs = Math.floor(totalSecs / 3600);
  const mins = Math.floor((totalSecs % 3600) / 60);
  const secs = totalSecs % 60;

  if (hrs > 0 || forceHours) {
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Helper to get fresh stream URL with latest auth token
 */
export function getGoogleDriveStreamUrl(fileId: string): string {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const tokenQuery = token ? `?t=${encodeURIComponent(token)}` : '';
  return API_BASE ? `${API_BASE}/api/audio/archivo/${fileId}${tokenQuery}` : `/api/audio/archivo/${fileId}${tokenQuery}`;
}

/**
 * Extracts Google Drive file ID from a raw ID or any standard Drive URL
 * and generates the stream, direct download and preview URLs.
 */
export function parseGoogleDriveAudioUrl(input: string | null | undefined): {
  fileId: string;
  streamUrl: string;
  directUrl: string;
  previewUrl: string;
} | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  let fileId = trimmed;

  // Check if it's a full Google Drive URL
  const fileDMatch = trimmed.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileDMatch && fileDMatch[1]) {
    fileId = fileDMatch[1];
  } else {
    const idParamMatch = trimmed.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (idParamMatch && idParamMatch[1]) {
      fileId = idParamMatch[1];
    }
  }

  // Basic validation for Google Drive file IDs (alphanumeric, underscores, dashes)
  if (!/^[a-zA-Z0-9_-]{10,}$/.test(fileId)) {
    return null;
  }

  const streamUrl = getGoogleDriveStreamUrl(fileId);

  return {
    fileId,
    streamUrl,
    directUrl: `https://drive.usercontent.google.com/download?id=${fileId}&export=download`,
    previewUrl: `https://drive.google.com/file/d/${fileId}/view`,
  };
}

export interface AudioPlayerProps {
  /** Google Drive file ID or URL */
  fileIdOrUrl: string | null | undefined;
  /** Optional title or label */
  title?: string;
  /** Estimated or DB-stored duration in seconds */
  initialDuration?: number | null;
  /** Compact presentation mode (e.g. for tables or cards) */
  compact?: boolean;
  /** Custom container class */
  className?: string;
  /** Show link to open in Google Drive */
  showDriveLink?: boolean;
  /** Callback fired when an error occurs during audio load or playback */
  onError?: (error: Error | MediaError | null, fileId: string) => void;
}

export function AudioPlayer({
  fileIdOrUrl,
  title,
  initialDuration,
  compact = false,
  className = '',
  showDriveLink = true,
  onError,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressBarId = useId();

  const driveData = useMemo(() => parseGoogleDriveAudioUrl(fileIdOrUrl), [fileIdOrUrl]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState<number>(initialDuration ?? 0);
  const [playbackRate, setPlaybackRate] = useState<number>(1);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const volumeControlRef = useRef<HTMLDivElement | null>(null);
  const [hasError, setHasError] = useState(false);
  const [bufferedPercent, setBufferedPercent] = useState(0);
  const [currentSrc, setCurrentSrc] = useState<string>(() => (driveData ? getGoogleDriveStreamUrl(driveData.fileId) : ''));

  // Único punto que escribe volume/muted en el elemento real. Antes se mutaban directamente en
  // dos lugares (handleToggleMute y el onChange de la barrita) con lógica duplicada, y se pisaban
  // entre sí — arrastrar a 0 después de silenciar dejaba audio.volume en un estado inconsistente.
  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = volume;
    audioRef.current.muted = isMuted;
  }, [volume, isMuted]);

  // Cierra la barrita de volumen al hacer clic fuera de ella — si no, se queda abierta encima del
  // resto del reproductor estorbando la barra de progreso.
  useEffect(() => {
    if (!showVolumeSlider) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (volumeControlRef.current && !volumeControlRef.current.contains(e.target as Node)) {
        setShowVolumeSlider(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showVolumeSlider]);

  // Sync initial duration if prop changes
  useEffect(() => {
    if (initialDuration && initialDuration > 0 && duration === 0) {
      setDuration(initialDuration);
    }
  }, [initialDuration, duration]);

  // Reset state if source changes
  useEffect(() => {
    setIsPlaying(false);
    setIsLoading(false);
    setCurrentTime(0);
    setHasError(false);
    setCurrentSrc(driveData ? getGoogleDriveStreamUrl(driveData.fileId) : '');
    if (initialDuration && initialDuration > 0) {
      setDuration(initialDuration);
    }
  }, [driveData?.fileId, initialDuration]);

  if (!driveData) {
    return null;
  }

  // Fallback: If loading audio failed, show an inline error panel with retry and Drive link
  if (hasError) {
    return (
      <div
        className={`p-3 bg-amber-50/90 border border-amber-200 rounded-xl space-y-2 font-['DM_Sans',sans-serif] w-full min-w-[300px] shadow-2xs ${className}`}
      >
        <div className="flex items-center justify-between gap-2 text-xs text-amber-900">
          <span className="font-medium truncate">{title ? `${title} — ` : ''}No se pudo reproducir el audio directamente</span>
          <a
            href={driveData.previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:text-blue-900 bg-white border border-blue-200 hover:bg-blue-50 px-2 py-1 rounded-md transition-colors shrink-0 shadow-2xs"
            title="Abrir audio en Google Drive"
          >
            <ExternalLink className="w-3 h-3" /> Abrir en Drive
          </a>
        </div>
        <div className="flex items-center gap-2 pt-1 border-t border-amber-200/60 text-[11px]">
          <button
            type="button"
            onClick={() => {
              setHasError(false);
              const freshUrl = getGoogleDriveStreamUrl(driveData.fileId);
              setCurrentSrc(freshUrl);
              if (audioRef.current) {
                audioRef.current.src = freshUrl;
                audioRef.current.load();
              }
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer shadow-2xs active:scale-95"
          >
            <RotateCcw className="w-3 h-3" /> Reintentar
          </button>
          <span className="text-amber-700/80 text-[11px]">
            Si tu navegador (Brave/Chrome) bloquea cookies de terceros, puedes abrir el enlace de Drive directamente.
          </span>
        </div>
      </div>
    );
  }

  const effectiveDuration = duration > 0 ? duration : (initialDuration ?? 0);
  const hasHours = effectiveDuration >= 3600;

  const handleTogglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      try {
        setIsLoading(true);
        if (driveData && (!currentSrc || currentSrc.includes('/api/audio/'))) {
          const freshSrc = getGoogleDriveStreamUrl(driveData.fileId);
          if (freshSrc !== currentSrc) {
            setCurrentSrc(freshSrc);
            audio.src = freshSrc;
          }
        }
        await audio.play();
        setIsPlaying(true);
      } catch (err) {
        console.error('[AudioPlayer] Error al reproducir audio desde Google Drive:', {
          fileId: driveData.fileId,
          src: currentSrc,
          error: err,
        });
        if (currentSrc.includes('/api/audio/') && driveData.directUrl) {
          // Try direct URL before failing
          setCurrentSrc(driveData.directUrl);
        } else {
          setHasError(true);
          onError?.(err instanceof Error ? err : new Error(String(err)), driveData.fileId);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const updateBufferedProgress = () => {
    const audio = audioRef.current;
    if (audio && audio.buffered.length > 0 && effectiveDuration > 0) {
      let currentBufferedEnd = 0;
      for (let i = 0; i < audio.buffered.length; i++) {
        const start = audio.buffered.start(i);
        const end = audio.buffered.end(i);
        if (audio.currentTime >= start && audio.currentTime <= end) {
          currentBufferedEnd = end;
          break;
        }
        if (end > currentBufferedEnd) {
          currentBufferedEnd = end;
        }
      }
      setBufferedPercent(Math.min(100, (currentBufferedEnd / effectiveDuration) * 100));
    }
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (audio) {
      setCurrentTime(audio.currentTime);
      if ((!initialDuration || initialDuration <= 0) && Number.isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
      updateBufferedProgress();
    }
  };

  const handleProgress = () => {
    updateBufferedProgress();
  };

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if ((!initialDuration || initialDuration <= 0) && audio && Number.isFinite(audio.duration) && audio.duration > 0) {
      setDuration(audio.duration);
    }
    updateBufferedProgress();
    setIsLoading(false);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    const targetTime = Number(e.target.value);
    setCurrentTime(targetTime);
    if (audio) {
      audio.currentTime = targetTime;
    }
  };

  const handleSkip = (seconds: number) => {
    const audio = audioRef.current;
    if (audio) {
      const newTime = Math.max(0, Math.min(audio.currentTime + seconds, effectiveDuration || Infinity));
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleCycleRate = () => {
    const rates = [1, 1.25, 1.5, 2];
    const nextIdx = (rates.indexOf(playbackRate) + 1) % rates.length;
    const nextRate = rates[nextIdx] ?? 1;
    setPlaybackRate(nextRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
  };

  // No muta audio.muted directamente — lo hace el efecto de arriba, único dueño de ese valor.
  const handleToggleMute = () => setIsMuted((prev) => !prev);

  const handleAudioError = (e: React.SyntheticEvent<HTMLAudioElement, Event>) => {
    const target = e.currentTarget;
    const mediaError = target.error;
    console.error('[AudioPlayer] Error al cargar o reproducir audio desde Google Drive:', {
      fileId: driveData.fileId,
      src: currentSrc,
      mediaError: mediaError ? { code: mediaError.code, message: mediaError.message } : 'Unknown error',
    });

    if (currentSrc.includes('/api/audio/stream/') && driveData.directUrl) {
      // Fallback from streamUrl to directUrl
      setCurrentSrc(driveData.directUrl);
    } else {
      setHasError(true);
      setIsPlaying(false);
      setIsLoading(false);
      onError?.(mediaError, driveData.fileId);
    }
  };

  const progressPercent = effectiveDuration > 0 ? (currentTime / effectiveDuration) * 100 : 0;
  const progressClamped = Math.min(100, Math.max(0, progressPercent));
  const bufferedClamped = Math.min(100, Math.max(progressClamped, bufferedPercent));

  if (compact) {
    return (
      <div
        className={`inline-flex items-center gap-3 px-3 py-2 bg-gray-50/90 border border-gray-200 rounded-xl text-xs w-full min-w-[300px] shadow-2xs font-['DM_Sans',sans-serif] ${className}`}
      >
        <audio
          ref={audioRef}
          src={currentSrc}
          preload="metadata"
          onTimeUpdate={handleTimeUpdate}
          onProgress={handleProgress}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setIsPlaying(false)}
          onError={handleAudioError}
          onWaiting={() => setIsLoading(true)}
          onCanPlay={() => setIsLoading(false)}
          onCanPlayThrough={updateBufferedProgress}
        />

        <button
          type="button"
          onClick={handleTogglePlay}
          disabled={isLoading && !isPlaying}
          aria-label={isPlaying ? 'Pausar audio' : 'Reproducir audio'}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-all shrink-0 shadow-xs active:scale-95 cursor-pointer"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-4 h-4 fill-current" />
          ) : (
            <Play className="w-4 h-4 fill-current ml-0.5" />
          )}
        </button>

        <div className="flex items-center gap-1 font-mono text-[11px] text-gray-700 shrink-0 tabular-nums select-none min-w-[110px] justify-center">
          <span className={hasHours ? 'min-w-[54px] text-right font-semibold' : 'min-w-[36px] text-right font-semibold'}>
            {formatAudioTime(currentTime, hasHours)}
          </span>
          <span className="text-gray-400">/</span>
          <span className={hasHours ? 'min-w-[54px] text-left text-gray-500' : 'min-w-[36px] text-left text-gray-500'}>
            {formatAudioTime(effectiveDuration, hasHours)}
          </span>
        </div>

        <div className="relative flex items-center flex-1 min-w-[120px] h-3 group">
          {/* Base and buffered tracks */}
          <div className="absolute inset-x-0 h-2 bg-gray-200 rounded-full overflow-hidden pointer-events-none">
            {/* Buffered track */}
            <div className="h-full bg-blue-300 rounded-full transition-[width] duration-200" style={{ width: `${bufferedClamped}%` }} />
            {/* Played track */}
            <div className="absolute inset-y-0 left-0 bg-blue-600 rounded-full" style={{ width: `${progressClamped}%` }} />
          </div>

          <input
            id={progressBarId}
            type="range"
            min={0}
            max={effectiveDuration || 100}
            step={0.5}
            value={currentTime}
            onChange={handleSeek}
            aria-label="Progreso de reproducción"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          />

          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-blue-600 rounded-full shadow-xs pointer-events-none -ml-1.5"
            style={{ left: `${progressClamped}%` }}
          />
        </div>

        <button
          type="button"
          onClick={handleCycleRate}
          className="px-2 py-1 text-[11px] font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer shrink-0"
          title="Velocidad de reproducción"
        >
          {playbackRate}x
        </button>

        {showDriveLink && (
          <a
            href={driveData.previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-blue-600 transition-colors p-1 shrink-0"
            title="Abrir en Google Drive"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>
    );
  }

  return (
    <div
      className={`p-4 bg-gray-50/90 border border-gray-200 rounded-2xl space-y-3 font-['DM_Sans',sans-serif] w-full min-w-[320px] shadow-2xs ${className}`}
    >
      <audio
        ref={audioRef}
        src={currentSrc}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onProgress={handleProgress}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        onError={handleAudioError}
        onWaiting={() => setIsLoading(true)}
        onCanPlay={() => setIsLoading(false)}
        onCanPlayThrough={updateBufferedProgress}
      />

      {title && (
        <div className="flex items-center justify-between gap-2 text-sm font-semibold text-gray-800">
          <span className="truncate">{title}</span>
          {showDriveLink && (
            <a
              href={driveData.previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[12px] text-blue-600 hover:underline shrink-0"
            >
              Drive <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      )}

      {/* Scrubber / timeline bar */}
      <div className="space-y-1.5">
        <div className="relative flex items-center w-full h-4 group">
          {/* Base and buffered tracks */}
          <div className="absolute inset-x-0 h-2.5 bg-gray-200 rounded-full overflow-hidden pointer-events-none">
            {/* Buffered track */}
            <div className="h-full bg-blue-300 rounded-full transition-[width] duration-200" style={{ width: `${bufferedClamped}%` }} />
            {/* Played track */}
            <div className="absolute inset-y-0 left-0 bg-blue-600 rounded-full" style={{ width: `${progressClamped}%` }} />
          </div>

          <input
            id={progressBarId}
            type="range"
            min={0}
            max={effectiveDuration || 100}
            step={0.5}
            value={currentTime}
            onChange={handleSeek}
            aria-label="Barra de progreso de audio"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          />

          <div
            className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-blue-600 rounded-full shadow-md pointer-events-none transition-transform group-hover:scale-110 -ml-1.75"
            style={{ left: `${progressClamped}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-[12px] font-mono text-gray-600 tabular-nums select-none px-0.5">
          <span className="font-semibold">{formatAudioTime(currentTime, hasHours)}</span>
          <span className="text-gray-500">{formatAudioTime(effectiveDuration, hasHours)}</span>
        </div>
      </div>

      {/* Control buttons */}
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-200/80">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleSkip(-30)}
            className="px-2 py-1 text-[11px] font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200/60 rounded-lg transition-colors"
            title="Retroceder 30s"
            aria-label="Retroceder 30 segundos"
          >
            -30s
          </button>

          <button
            type="button"
            onClick={() => handleSkip(-10)}
            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200/60 rounded-lg transition-colors"
            title="Retroceder 10s"
            aria-label="Retroceder 10 segundos"
          >
            <RotateCcw className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={handleTogglePlay}
            disabled={isLoading && !isPlaying}
            aria-label={isPlaying ? 'Pausar audio' : 'Reproducir audio'}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-all shadow-md active:scale-95 cursor-pointer mx-1"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : isPlaying ? (
              <Pause className="w-5 h-5 fill-current" />
            ) : (
              <Play className="w-5 h-5 fill-current ml-0.5" />
            )}
          </button>

          <button
            type="button"
            onClick={() => handleSkip(10)}
            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200/60 rounded-lg transition-colors"
            title="Adelantar 10s"
            aria-label="Adelantar 10 segundos"
          >
            <FastForward className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={() => handleSkip(30)}
            className="px-2 py-1 text-[11px] font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200/60 rounded-lg transition-colors"
            title="Adelantar 30s"
            aria-label="Adelantar 30 segundos"
          >
            +30s
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleCycleRate}
            className="px-2.5 py-1 text-[12px] font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer shadow-2xs"
            title="Cambiar velocidad de reproducción"
          >
            {playbackRate}x
          </button>

          <div className="relative" ref={volumeControlRef}>
            {showVolumeSlider && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 flex flex-col items-center gap-1.5 bg-white border border-gray-200 rounded-lg shadow-lg py-2.5 px-1.5">
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={isMuted ? 0 : volume}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setVolume(next);
                    setIsMuted(next === 0);
                  }}
                  className="w-1.5 h-20 cursor-pointer accent-blue-600"
                  style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
                  title="Volumen"
                  aria-label="Volumen"
                />
                <span className="text-[10px] font-semibold text-gray-500 tabular-nums">{Math.round((isMuted ? 0 : volume) * 100)}%</span>
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                handleToggleMute();
                setShowVolumeSlider((v) => !v);
              }}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200/60 rounded-lg transition-colors cursor-pointer"
              title={isMuted ? 'Desactivar silencio' : 'Silenciar'}
              aria-label={isMuted ? 'Desactivar silencio' : 'Silenciar'}
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="w-4 h-4 text-red-500" />
              ) : volume < 0.5 ? (
                <Volume1 className="w-4 h-4" />
              ) : (
                <Volume2 className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
